import { z } from 'zod';

/**
 * Platform (kiraci ustu) katman.
 *
 * Adres semasi:
 *   otelcms.com            -> tanitim sayfasi (landing)
 *   otelcms.com/admin      -> platform yonetim paneli (yetkili)
 *   <slug>.otelcms.com     -> ilgili tesisin uygulama paneli
 *   demo.otelcms.com       -> demo tesisi (slug = "demo")
 *
 * Kiraci COZUMLEMESI HER ZAMAN HOST'TAN yapilir. Istemcinin gonderdigi
 * tenantId'ye guvenilmez; aksi halde bir otelin kullanicisi baska bir otelin
 * verisine erisebilir.
 */

export const PLATFORM_ROLLERI = ['SUPERADMIN', 'DESTEK', 'SATIS'] as const;
export const platformRolSchema = z.enum(PLATFORM_ROLLERI);
export type PlatformRol = z.infer<typeof platformRolSchema>;

export const PLATFORM_YETKILERI = [
  'tesis:oku',
  'tesis:olustur',
  'tesis:guncelle',
  'tesis:askiya_al',
  'tesis:sil',
  'tesis:parola_sifirla',
  'platform:kullanici_yonet',
  'platform:denetim_oku',
] as const;
export const platformYetkiSchema = z.enum(PLATFORM_YETKILERI);
export type PlatformYetki = z.infer<typeof platformYetkiSchema>;

export const PLATFORM_ROL_YETKILERI: Record<PlatformRol, readonly PlatformYetki[]> = {
  SUPERADMIN: [...PLATFORM_YETKILERI],
  // Satis ekibi tesis acabilir ama silemez / kullanici yonetemez
  SATIS: ['tesis:oku', 'tesis:olustur', 'tesis:guncelle'],
  // Destek yalnizca gorur ve parola sifirlar
  DESTEK: ['tesis:oku', 'tesis:parola_sifirla', 'platform:denetim_oku'],
};

export function platformYetkiVarMi(rol: PlatformRol, yetki: PlatformYetki): boolean {
  return PLATFORM_ROL_YETKILERI[rol].includes(yetki);
}

/* ------------------------------------------------------------------ slug */

/**
 * Tesise ayrilamayacak alt alan adlari.
 * Sebep: ya platformun kendisi kullanir, ya e-posta/SSL dogrulamasini bozar,
 * ya da kullaniciyi yaniltir (ornegin "guvenlik.otelcms.com").
 */
export const AYRILMIS_SLUGLAR = new Set([
  'www', 'api', 'admin', 'app', 'panel', 'portal', 'dashboard',
  'mail', 'email', 'smtp', 'imap', 'pop', 'mx', 'webmail', 'autodiscover', 'autoconfig',
  'ns', 'ns1', 'ns2', 'dns', 'cdn', 'static', 'assets', 'img', 'media', 'files',
  'blog', 'docs', 'doc', 'dokuman', 'dokumanlar', 'destek', 'support', 'help', 'yardim',
  'status', 'durum', 'health', 'metrics', 'log', 'logs',
  'login', 'giris', 'auth', 'oauth', 'sso', 'id', 'account', 'hesap',
  'billing', 'fatura', 'odeme', 'pay', 'payment', 'checkout',
  'test', 'dev', 'staging', 'stage', 'prod', 'production', 'preview', 'beta',
  'guvenlik', 'security', 'abuse', 'postmaster', 'hostmaster', 'webmaster',
  'kbs', 'ota', 'channel', 'kanal', 'webhook', 'webhooks', 'cron', 'internal',
]);

/**
 * Alt alan adi kurallari:
 *  - 3-30 karakter, kucuk harf/rakam/tire
 *  - tire ile baslayamaz/bitemez, ust uste tire olamaz
 *  - "xn--" ile baslayamaz (punycode karisikligi)
 *  - tamami rakam olamaz (IP benzeri adres karisikligi)
 */
export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'En az 3 karakter olmalı')
  .max(30, 'En fazla 30 karakter olabilir')
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Yalnızca küçük harf, rakam ve tek tire kullanın')
  .refine((s) => !AYRILMIS_SLUGLAR.has(s), 'Bu adres platform tarafından ayrılmış')
  .refine((s) => !s.startsWith('xn--'), 'Bu adres kullanılamaz')
  .refine((s) => !/^\d+$/.test(s), 'Adres yalnızca rakamlardan oluşamaz');

/** "Ala Otel Akyaka" -> "ala-otel-akyaka" */
export function slugOner(ad: string): string {
  return ad
    .toLowerCase()
    .replaceAll('ı', 'i').replaceAll('İ', 'i')
    .replaceAll('ş', 's').replaceAll('Ş', 's')
    .replaceAll('ğ', 'g').replaceAll('Ğ', 'g')
    .replaceAll('ü', 'u').replaceAll('Ü', 'u')
    .replaceAll('ö', 'o').replaceAll('Ö', 'o')
    .replaceAll('ç', 'c').replaceAll('Ç', 'c')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30)
    .replace(/-+$/, '');
}

/* ------------------------------------------------------------------ host */

export type HostTipi = 'APEX' | 'TENANT' | 'GECERSIZ';

export interface HostCozumu {
  tip: HostTipi;
  slug: string | null;
  /** Platform alan adi disindaki host (workers.dev, localhost) */
  platformDisi: boolean;
}

/**
 * Host basligindan kiraci cozumler.
 * `platformDomain` ornegin "otelcms.com".
 */
export function hostCozumle(hostBasligi: string | null | undefined, platformDomain: string): HostCozumu {
  const host = (hostBasligi ?? '').toLowerCase().split(':')[0]?.trim() ?? '';
  if (!host) return { tip: 'GECERSIZ', slug: null, platformDisi: true };

  const domain = platformDomain.toLowerCase();

  if (host === domain || host === `www.${domain}`) {
    return { tip: 'APEX', slug: null, platformDisi: false };
  }

  if (host.endsWith(`.${domain}`)) {
    const on = host.slice(0, -(domain.length + 1));
    // Cok seviyeli alt alan adi (a.b.otelcms.com) desteklenmez: Universal SSL
    // yalnizca tek seviyeyi kapsar ve karisiklik yaratir.
    if (on.includes('.')) return { tip: 'GECERSIZ', slug: null, platformDisi: false };
    if (AYRILMIS_SLUGLAR.has(on)) return { tip: 'APEX', slug: null, platformDisi: false };
    return { tip: 'TENANT', slug: on, platformDisi: false };
  }

  // workers.dev / localhost gibi platform disi adresler: gelistirme ve
  // staging icin APEX kabul edilir, kiraci basligi ayrica verilebilir.
  return { tip: 'APEX', slug: null, platformDisi: true };
}

/* ------------------------------------------------------- tesis kurulumu */

export const PLANLAR = ['DENEME', 'BASLANGIC', 'PROFESYONEL', 'KURUMSAL'] as const;
export const planSchema = z.enum(PLANLAR);
export type Plan = z.infer<typeof planSchema>;

export const PLAN_OZELLIKLERI: Record<Plan, { odaLimiti: number; ocrGunlukKota: number; aciklama: string }> = {
  DENEME: { odaLimiti: 20, ocrGunlukKota: 50, aciklama: '14 gun ucretsiz deneme' },
  BASLANGIC: { odaLimiti: 30, ocrGunlukKota: 200, aciklama: 'Pansiyon / apart' },
  PROFESYONEL: { odaLimiti: 80, ocrGunlukKota: 500, aciklama: 'Butik otel' },
  KURUMSAL: { odaLimiti: 250, ocrGunlukKota: 2000, aciklama: 'Zincir / buyuk tesis' },
};

export const TESIS_DURUMLARI = ['DENEME', 'AKTIF', 'ASKIDA', 'IPTAL'] as const;
export const tesisDurumSchema = z.enum(TESIS_DURUMLARI);
export type TesisDurum = z.infer<typeof tesisDurumSchema>;

export const KBS_SAGLAYICILARI = ['RESMI_WS', 'MANUEL_FORM', 'KAPALI'] as const;
export const kbsSaglayiciSchema = z.enum(KBS_SAGLAYICILARI);
export type KbsSaglayici = z.infer<typeof kbsSaglayiciSchema>;

/**
 * KBS baglanti bilgilerinin alan kurallari.
 * Kurulum formu ile sonradan guncelleme ayni kurallari kullansin diye
 * tek yerde durur; biri degisirse digeri sessizce geride kalmaz.
 */
const KBS_ALANLARI = {
  kbsSaglayici: kbsSaglayiciSchema,
  kbsTesisKodu: z.string().trim().max(60).nullable(),
  kbsKullanici: z.string().trim().max(120).nullable(),
  kbsSifre: z.string().max(200).nullable(),
};

/**
 * Kurulmus bir tesisin KBS bilgilerini sonradan degistirme sozlesmesi.
 * Her alan istege bagli: yalnizca gonderilen alanlar guncellenir.
 * `kbsSifre` yalnizca yazilir - hicbir uctan duz metin geri donmez.
 */
export const tesisKbsGuncelleSchema = z.object({
  kbsSaglayici: KBS_ALANLARI.kbsSaglayici.optional(),
  kbsTesisKodu: KBS_ALANLARI.kbsTesisKodu.optional(),
  kbsKullanici: KBS_ALANLARI.kbsKullanici.optional(),
  kbsSifre: KBS_ALANLARI.kbsSifre.optional(),
});
export type TesisKbsGuncelle = z.infer<typeof tesisKbsGuncelleSchema>;

/** Admin panelindeki "Yeni tesis" formunun sozlesmesi. */
export const tesisKurSchema = z.object({
  ad: z.string().trim().min(2, 'Tesis adı en az 2 karakter').max(120),
  slug: slugSchema,
  tip: z.enum(['OTEL', 'BUTIK', 'APART', 'PANSIYON']).default('BUTIK'),
  plan: planSchema.default('DENEME'),

  // Iletisim / fatura
  vergiNo: z.string().trim().max(20).nullable().default(null),
  adres: z.string().trim().max(300).nullable().default(null),
  telefon: z.string().trim().max(30).nullable().default(null),
  eposta: z.string().email('Geçerli bir e-posta girin'),

  // Tesis yoneticisi (OWNER) hesabi
  yoneticiAd: z.string().trim().min(2, 'Yetkili adı gerekli').max(120),
  yoneticiEposta: z.string().email('Geçerli bir e-posta girin'),

  // KBS bilgileri (istege bagli; PATCH /api/admin/tesisler/:id ile sonradan da girilir)
  kbsSaglayici: KBS_ALANLARI.kbsSaglayici.default('KAPALI'),
  kbsTesisKodu: KBS_ALANLARI.kbsTesisKodu.default(null),
  kbsKullanici: KBS_ALANLARI.kbsKullanici.default(null),
  kbsSifre: KBS_ALANLARI.kbsSifre.default(null),

  // Baslangic envanteri (istege bagli): "10 STD, 6 DLX" gibi hizli kurulum
  odaTipleri: z
    .array(
      z.object({
        kod: z.string().trim().min(1).max(10).toUpperCase(),
        ad: z.string().trim().min(2).max(80),
        adet: z.number().int().min(0).max(500),
        maksYetiskin: z.number().int().min(1).max(10).default(2),
        gecelikFiyat: z.number().min(0).max(1_000_000).default(0),
      }),
    )
    .max(10)
    .default([]),

  notlar: z.string().max(2000).nullable().default(null),
});
export type TesisKur = z.infer<typeof tesisKurSchema>;

export const tesisKurSonucSchema = z.object({
  tenantId: z.string(),
  slug: z.string(),
  url: z.string(),
  yoneticiEposta: z.string(),
  /** Yalnizca bir kez gosterilir, hicbir yerde duz saklanmaz */
  geciciParola: z.string(),
  olusanOda: z.number(),
  olusanOdaTipi: z.number(),
  /** Alt alan adinin acilip acilmadigi - panelde gosterilir */
  alanAdi: z.object({
    durum: z.enum(['ACILDI', 'YAPILANDIRILMAMIS', 'HATA']),
    hostname: z.string(),
    mesaj: z.string().nullable(),
  }),
});
export type TesisKurSonuc = z.infer<typeof tesisKurSonucSchema>;

/** Platform yoneticisi JWT govdesi (kiraci JWT'sinden ayridir). */
export const platformJwtSchema = z.object({
  sub: z.string(),
  rol: platformRolSchema,
  typ: z.enum(['platform-access', 'platform-refresh']),
  iat: z.number(),
  exp: z.number(),
  jti: z.string(),
});
export type PlatformJwt = z.infer<typeof platformJwtSchema>;

export const PLATFORM_ACCESS_OMRU_SN = 30 * 60; // 30 dk
export const PLATFORM_REFRESH_OMRU_SN = 12 * 60 * 60; // 12 saat - panel gunluk kullanilir
